'use client'

import GenericForm from 'components/Form'
import { signIn } from 'next-auth/react'
import { useState } from 'react'
import { object, string } from 'yup'
import { AuthRequestOtp, AuthVerifyOtp } from 'app/api/auth'

interface OtpLoginFormProps {
    lang: string
    texts: {
        email: string
        otpCode: string
        login: string
        loggingIn: string
        requestOtp: string
        requestingOtp: string
        verifyOtp: string
        verifyingOtp: string
        enterOtpCode: string
        otpSent: string
        loggedInSuccessfullyRedirecting: string
        invalidOtp: string
    }
    className?: string
}

const OtpLoginForm = ({ lang, texts }: OtpLoginFormProps) => {
    const [otpSent, setOtpSent] = useState(false)
    const [email, setEmail] = useState('')

    const handleRequestOtp = async (formData: FormData): Promise<ApiResponse> => {
        const emailValue = formData.get('email')?.toString()
        if (!emailValue) return { success: false, message: 'Email is required' }

        const response = await AuthRequestOtp({ email: emailValue }, '')
        if (response.success) {
            setEmail(emailValue)
            setOtpSent(true)
        }
        return response
    }

    const handleVerifyOtp = async (formData: FormData): Promise<ApiResponse> => {
        const otpCode = formData.get('otpCode')?.toString()
        if (!otpCode || !email) return { success: false, message: 'OTP code is required' }

        try {
            const verifyResponse = await AuthVerifyOtp({ email, otpCode }, '')
            
            if (verifyResponse.success && verifyResponse.data?.recoveryToken) {
                // After OTP verification, use the recoveryToken to authenticate
                // The authOptions has been modified to accept OTP tokens with "otp-token:" prefix
                const signInResult = await signIn('credentials', {
                    username: email,
                    password: `otp-token:${verifyResponse.data.recoveryToken}`,
                    redirect: false,
                    callbackUrl: `/callbackUrl`
                })

                const result: ApiResponse = {
                    success: signInResult?.ok ?? false,
                    data: signInResult,
                    error: signInResult?.error ?? undefined,
                    message: signInResult?.error ? signInResult.error : texts.loggedInSuccessfullyRedirecting
                }

                if (result.success) {
                    // Wait for session cookie to be set by NextAuth, then do full page reload
                    // This ensures RootLayout (server component) re-fetches the user via getUserSession
                    // and updates SessionProvider, making user available to RightNavbarSliderMenu
                    // Using a delay to ensure the cookie is properly set before redirect
                    await new Promise(resolve => setTimeout(resolve, 500))
                    
                    // Full page reload ensures all server components re-run and read the session cookie
                    window.location.href = `/${lang}/home`
                }
                return result
            }

            return {
                success: false,
                message: verifyResponse.message || texts.invalidOtp
            }
        } catch (error) {
            return {
                success: false,
                message: error instanceof Error ? error.message : 'OTP verification failed'
            }
        }
    }

    const emailSchema = object({
        email: string()
            .required('Please enter your email')
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
    })

    const otpSchema = object({
        otpCode: string()
            .required(texts.enterOtpCode)
            .matches(/^\d{6}$/, 'OTP code must be 6 digits')
    })

    if (!otpSent) {
        const fields: TFormField[] = [
            {
                name: 'email',
                label: texts.email,
                type: 'email',
                required: true,
                placeholder: 'Enter your email'
            }
        ]

        return (
            <div>
                <GenericForm
                    fields={fields}
                    onSubmit={handleRequestOtp}
                    buttonTexts={{ save: texts.requestOtp, saving: texts.requestingOtp }}
                    validationSchema={emailSchema}
                />
            </div>
        )
    }

    const fields: TFormField[] = [
        {
            name: 'otpCode',
            label: texts.otpCode,
            type: 'text',
            required: true,
            placeholder: texts.enterOtpCode,
            maxLength: 6
        }
    ]

    return (
        <div>
            <div className='mb-4 text-sm text-muted-foreground'>
                {texts.otpSent} {email}
            </div>
            <GenericForm
                fields={fields}
                onSubmit={handleVerifyOtp}
                buttonTexts={{ save: texts.verifyOtp, saving: texts.verifyingOtp }}
                validationSchema={otpSchema}
                keepLoadingOnSuccess={true}
            />
            <button
                type='button'
                onClick={() => {
                    setOtpSent(false)
                    setEmail('')
                }}
                className='mt-4 text-sm text-muted-foreground hover:text-foreground underline'
            >
                Use a different email
            </button>
        </div>
    )
}

export default OtpLoginForm
